// @src/hooks/useApi.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import useZustand from 'hooks/useZustand';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import consoleX from 'utils/consoleX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Global map to track executed requests and prevent duplicates
const executionTracker = new Map();

// Global Set to track blocked execution keys during page transitions
// Once a component is blocked during transition, it stays blocked for the entire cycle
// This prevents old page components from re-executing when storedTransition changes
const blockedDuringTransition = new Set();

// Global Set to track failed API requests that should never retry
// This prevents infinite loops when API returns 404, 403, etc.
const failedRequests = new Set();

// Global variable to track last module ID across component remounts
// This persists even when components unmount/remount during navigation
let globalLastModuleId = null;

// Global variable to track last page name within the same module
// This ensures tracker is cleared when navigating between pages (init -> document)
let globalLastPageName = null;

// Global set to track modules that have already been confirmed and executed
// This prevents re-execution when component remounts but store still has old module data
const globalConfirmedModules = new Set();

// Timestamp to track when PUT/POST just updated the store
// This prevents useApi from refetching data that was just saved
let lastSaveTimestamp = 0;

// Export function to set the save timestamp (called from useApiInitializer after PUT/POST)
export const markJustSaved = () => {
  lastSaveTimestamp = Date.now();
};

// Check if we just saved (within 1 second)
const wasJustSaved = () => {
  return Date.now() - lastSaveTimestamp < 1000;
};

// Timestamp to track when a manual refresh (GET) was triggered
// This prevents useApi from triggering a duplicate GET when the first one updates the store
let lastRefreshTimestamp = 0;

// Export function to set the refresh timestamp (called from useApiInitializer when get is called with showLoading)
export const markJustRefreshed = () => {
  lastRefreshTimestamp = Date.now();
};

// Check if we just refreshed (within 500ms - shorter window since GET is faster)
const wasJustRefreshed = () => {
  return Date.now() - lastRefreshTimestamp < 500;
};

// *******************************************************************************
// EXPORTED FUNCTIONS (for useApiInitializer)
// *******************************************************************************
export const markRequestAsFailed = (paramKey, _id, selectedTab) => {
  const executionKey = `${paramKey || 'none'}-${_id || 'none'}-${selectedTab || 'none'}`;
  failedRequests.add(executionKey);
};

// Clear all execution trackers - called by abortDocumentAccess and signOut to ensure clean state
export const clearAllExecutionTrackers = () => {
  executionTracker.clear();
  blockedDuringTransition.clear();
  failedRequests.clear();
  globalConfirmedModules.clear();
  globalLastModuleId = null;
  globalLastPageName = null;
};

// *******************************************************************************
// HOOKS
// *******************************************************************************
const useApi = ({
  hideLoading = false,
  paramKey = undefined,
  autoFetch = undefined, // If undefined, inherits from storedCurrentPage?.api?.autoFetch
  url = undefined,
  selectedTab = undefined,
  params: propParams = undefined, // Allow passing params directly (needed when called from DocumentProvider)
}) => {
  // const useApi = (done = false) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { params: contextParams } = useDocumentContext();
  // Use propParams if provided (from DocumentProvider), otherwise use context params
  const params = propParams || contextParams;
  const apiGlobal = useZustand('global', 'useApi');
  const storedReady = useZustand('module', 'ready');
  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedTransition = useZustand('module', 'transition');
  const setTransitionStore = useZustand('module', 'setTransition');
  const storedSubscription = useZustand('auth', 'subscription');
  const storedCompany = useZustand('auth', 'company');

  // Resolve autoFetch: parameter > config > default true
  const resolvedAutoFetch = autoFetch ?? storedCurrentPage?.api?.autoFetch ?? true;

  // Check if page requires company filter
  const filterByCompany = storedCurrentPage?.settings?.filterByCompany;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Two-phase module confirmation pattern to handle navigation transitions:
  // - pendingModuleRef: tracks candidate module from previous effect run
  // - confirmedModuleRef: tracks the confirmed (stable) module after 2 cycles
  // - confirmationTrigger: forces re-render to trigger effect again after setting pending
  // This prevents executing with stale store data during navigation
  const pendingModuleRef = useRef(null);
  const confirmedModuleRef = useRef(null);
  const [confirmationTrigger, setConfirmationTrigger] = useState(0);

  // CRITICAL: Reset refs AND clear tracker when moduleId changes
  // Uses globalLastModuleId (module-level) instead of ref to persist across component remounts
  const currentModuleId = storedCurrentPage?.moduleId;
  const currentPageName = storedCurrentPage?.name;
  const currentModulePage =
    currentModuleId && currentPageName ? `${currentModuleId}|${currentPageName}` : null;

  if (currentModuleId && globalLastModuleId !== currentModuleId) {
    // Clear tracker entries for BOTH old and new module to allow fresh execution
    for (const key of executionTracker.keys()) {
      if (key.startsWith(`${globalLastModuleId}-`) || key.startsWith(`${currentModuleId}-`)) {
        executionTracker.delete(key);
      }
    }

    // Remove old module from confirmed set to allow re-execution if user returns
    globalConfirmedModules.delete(globalLastModuleId);

    globalLastModuleId = currentModuleId;
    globalLastPageName = currentPageName;
    pendingModuleRef.current = null;
    confirmedModuleRef.current = null;
  }
  // FIX: Also clear tracker when PAGE changes within the same module (e.g., init -> document)
  else if (currentModuleId && currentPageName && globalLastPageName !== currentPageName) {
    // Clear tracker entries for THIS module to allow fresh execution on new page
    for (const key of executionTracker.keys()) {
      if (key.startsWith(`${currentModuleId}-`)) {
        executionTracker.delete(key);
      }
    }

    globalLastPageName = currentPageName;
    pendingModuleRef.current = null;
    confirmedModuleRef.current = null;
  }
  // If module is already globally confirmed but refs are null (component remounted),
  // restore refs (but NOT tracker - that could block legitimate requests)
  else if (
    currentModulePage &&
    globalConfirmedModules.has(currentModuleId) &&
    confirmedModuleRef.current === null
  ) {
    pendingModuleRef.current = currentModulePage;
    confirmedModuleRef.current = currentModulePage;
    // FIX: Do NOT set tracker entry here - it uses wrong params (none-none-none)
    // and blocks legitimate requests with actual paramKey/_id values
    // The tracker will be set correctly by the effect when it executes
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────

  // DISABLED: Clearing tracker on page change causes old components to re-execute during navigation
  // The tracker cleanup now relies only on component unmount (line 120-128) and conditional clearing (line 66-70)
  // useEffect(() => {
  //   const pageName = storedCurrentPage?.name;
  //   if (!pageName) return;
  //
  //   // Clear all entries in the tracker when page changes
  //   executionTracker.clear();
  // }, [storedCurrentPage?.name]);

  useEffect(() => {
    // Create execution key early for blocking checks
    const _id = params?.[paramKey];
    // Include moduleId AND pageName so navigation between modules/pages creates different keys
    const moduleId = storedCurrentPage?.moduleId;
    const pageName = storedCurrentPage?.name;
    const executionKey = `${moduleId || 'none'}-${pageName || 'none'}-${paramKey || 'none'}-${_id || 'none'}-${selectedTab || 'none'}`;
    const apiUrl = url || storedCurrentPage?.api?.url;

    // Skip if module/page info not yet loaded
    if (!moduleId || !pageName) return;

    // CRITICAL: If login expired, abort all API calls to prevent infinite loops
    // If fetchInterceptor blocks request at line 336, we must not retry
    try {
      const loginExpired =
        typeof localStorage !== 'undefined' && localStorage.getItem('loginExpired') === 'true';
      if (loginExpired) {
        return;
      }
    } catch {}

    // CRITICAL: Check apiGlobal BEFORE module confirmation
    // After F5, apiGlobal might not be ready yet, but the store might have stale data.
    // If we confirm the module before checking apiGlobal, we'll confirm with stale data
    // and block all future runs with correct data.
    if (!apiGlobal?.done && !apiGlobal?.get) return;

    // CRITICAL: Two-phase module confirmation to handle navigation transitions
    // This prevents executing with stale store data when navigating between modules
    const currentModulePage = `${moduleId}|${pageName}`;

    // Phase 1: If already confirmed, check for stale component
    if (confirmedModuleRef.current !== null) {
      if (confirmedModuleRef.current !== currentModulePage) {
        return; // Stale component - module changed after confirmation
      }
      // Confirmed and matches - continue to execution
    }
    // Phase 2: Not yet confirmed - need to verify module stability
    else {
      if (pendingModuleRef.current === null) {
        // First run: set pending and trigger re-render for confirmation
        pendingModuleRef.current = currentModulePage;
        setConfirmationTrigger(prev => prev + 1); // Force effect to re-run
        return;
      } else if (pendingModuleRef.current !== currentModulePage) {
        // Module changed - update pending and trigger re-render
        pendingModuleRef.current = currentModulePage;
        setConfirmationTrigger(prev => prev + 1); // Force effect to re-run
        return;
      } else {
        // Module stable (same as pending) - confirm and continue
        confirmedModuleRef.current = currentModulePage;
        globalConfirmedModules.add(moduleId); // Mark as globally confirmed
      }
    }

    // CRITICAL: Check if _id is a URL template (contains ":" like ":userId" or ":_id")
    // This happens when URL params are not properly replaced during navigation
    // if (_id && typeof _id === 'string' && _id.includes(':')) {
    //   console.log('🚫 useApi BLOCKED: _id is URL template', { paramKey, _id, selectedTab });
    //   return;
    // }

    // CRITICAL: Check if this execution was blocked during transition
    // Once blocked, stay blocked for the entire transition cycle
    if (blockedDuringTransition.has(executionKey)) return;

    // CRITICAL: Check if this request failed before (404, 403, etc.)
    // Never retry failed requests to prevent infinite loops
    if (failedRequests.has(executionKey)) return;

    // Calculate if this effect would call get() (vs just done())
    // Tabs that only call done() should not be blocked by save/refresh guards
    const wouldCallGet = paramKey && apiUrl && _id && _id !== 'add';

    // CRITICAL: Skip refetch if we just saved (PUT/POST updated the store)
    // This prevents the flash/reload after saving
    // Only skip for effects that would call get()
    if (wasJustSaved() && wouldCallGet) return;

    // CRITICAL: Skip refetch if we just refreshed (manual GET updated the store)
    // This prevents duplicate GET requests when RefreshButton triggers a refresh
    // Only skip for effects that would call get()
    if (wasJustRefreshed() && wouldCallGet) return;

    // CRITICAL: Page transition detection using explicit from/to tracking
    const transition = storedTransition; // {from: 'init', to: 'subscription'} or null

    // CRITICAL: If transition is active, validate current page
    if (transition) {
      // CRITICAL: Handle invalid transition where from === to
      // This happens when abortDocumentAccess navigates directly via navigate() without updating currentPage
      // In this case, clear transition and continue execution normally
      if (transition.from === transition.to) {
        if (setTransitionStore) {
          setTransitionStore(null);
        }
        // Clear any stale blocked executions
        setTimeout(() => {
          blockedDuringTransition.clear();
          failedRequests.clear();
        }, 0);
        // Continue execution - don't return
      } else {
        const isOldPage = pageName === transition.from;
        const isNewPage = pageName === transition.to;

        // Block old page components
        if (isOldPage) {
          blockedDuringTransition.add(executionKey);
          return;
        }

        // If this is the new page, clear transition and allow execution
        if (isNewPage) {
          if (setTransitionStore) {
            setTransitionStore(null);
          }
          // Clear blocked executions and failed requests after transition completes
          setTimeout(() => {
            blockedDuringTransition.clear();
            failedRequests.clear();
          }, 0);
        }
      }
    } else {
      // CRITICAL: No active transition - clear any stale blocked executions
      // This can happen after abortDocumentAccess which navigates directly without setPage,
      // causing transition to never be set, leaving blockedDuringTransition with stale entries
      if (blockedDuringTransition.size > 0 || failedRequests.size > 0) {
        blockedDuringTransition.clear();
        failedRequests.clear();
      }
    }

    const hasData = !!storedCurrentPage?.data;
    const hasTracker = executionTracker.has(executionKey);

    // CRITICAL: If data is null AND hideLoading is true, ALWAYS clear the tracker
    // This ensures data is fetched when returning from another page via setPageBack
    // Only clear if paramKey exists (has data expectations) to prevent duplicate execution for tabs without data
    if (!hasData && hasTracker && hideLoading && paramKey) {
      executionTracker.delete(executionKey);
    }

    // Check if this exact execution already happened
    if (executionTracker.has(executionKey)) return;

    if (paramKey && !_id) return;
    // NOTE: apiGlobal check moved to line 197-201 (BEFORE module confirmation)
    // to prevent confirming module with stale data after F5

    // Skip execution when autoFetch is explicitly false
    // This means the page component is responsible for handling its own API calls
    // EXCEPTION 1: paramKey === null means "explicitly marking ready only, not fetching"
    //              Tabs pass paramKey: null to indicate they just want to call done(), not fetch
    // EXCEPTION 2: apiUrl is null/undefined means page has no API endpoint
    //              These pages should still call done() to be marked as ready
    if (resolvedAutoFetch === false && paramKey !== null && apiUrl) return;

    // CRITICAL: For SCENARIO 2 (document fetch with _id), wait for subscription before proceeding
    // Check this BEFORE adding to tracker to allow re-execution when subscription loads
    // EXCEPTION: Pages with fetchWithoutSubscription: true don't need subscription
    const fetchWithoutSubscription = storedCurrentPage?.api?.fetchWithoutSubscription;
    const isScenario2 = _id && _id !== 'add' && paramKey && apiUrl;
    if (isScenario2 && !fetchWithoutSubscription && !storedSubscription?._id) {
      return; // Don't add to tracker - allow re-execution when subscription loads
    }

    // NOTE: filterByCompany verification for LIST pages is handled by ListProvider.jsx
    // useApi only handles DOCUMENT pages (with _id), which don't typically use filterByCompany
    // For document pages that DO need filterByCompany, the check is below (only for isScenario2)
    if (isScenario2 && filterByCompany && !storedCompany?._id) {
      return; // Don't add to tracker - allow re-execution when company loads
    }

    // CRITICAL: If page expects params (has :_id in URL) but params is undefined, skip execution
    // This happens during initial render when useUrl() hasn't returned params yet
    // Check this BEFORE adding to tracker to allow re-execution when params become available
    const pageExpectsId = apiUrl && apiUrl.includes(':_id');
    if (pageExpectsId && !params) {
      return; // Don't add to tracker - allow re-execution when params become available
    }

    // CRITICAL: If URL has a document _id but paramKey is null/undefined, skip execution
    // This happens during navigation when storedCurrentPage still has old page's config
    // Wait for the correct page config to load before executing
    const urlHasDocumentId = params?._id && params._id !== 'add';
    if (urlHasDocumentId && !paramKey) {
      return; // Don't add to tracker - allow re-execution when correct config loads
    }

    // Add to tracker BEFORE executing to prevent duplicates
    executionTracker.set(executionKey, true);

    // Get config params, query, and returnAsObject for the request
    const configParams = storedCurrentPage?.api?.params;
    const configQuery = storedCurrentPage?.api?.query;
    const configReturnAsObject = storedCurrentPage?.api?.returnAsObject;

    // SCENARIO 1: _id === 'add' OR no paramKey OR no apiUrl
    // → Call done() to mark ready (hideLoading determines if loading screen is hidden)
    // NOTE: urlHasDocumentId check is done BEFORE adding to tracker (line 365-373)
    if (_id === 'add' || !paramKey || !apiUrl) {
      if (apiGlobal?.done) {
        // For tabs: hideLoading = true only for current tab, false for others
        // This allows each tab to register but only current tab hides loading
        // IMPORTANT: Pages with no apiUrl should always hide loading (nothing to fetch)
        const shouldHideLoading = hideLoading || _id === 'add' || !apiUrl;

        // For list pages with apiUrl, loading will be hidden by get() call from ListProvider
        // Show success in log because we know loading WILL be hidden
        const willHideLoading = shouldHideLoading || (apiUrl && !paramKey && resolvedAutoFetch);

        if (!storedReady) {
          consoleX({
            message: `${moduleId}|${pageName}` + (selectedTab ? `|${selectedTab}` : ''),
            emoji: willHideLoading ? '✅' : '⏱️',
            flag: willHideLoading ? 'success' : 'default',
            title: 'DONE',
            info: {
              module: moduleId,
              page: pageName,
              tab: selectedTab,
            },
          });
        }

        apiGlobal.done(shouldHideLoading);
      }
    } else {
      // SCENARIO 2: autoFetch: true + apiUrl exists + has _id and paramKey
      // → Call get() with params and query from config
      // NOTE: Subscription check is done BEFORE adding to tracker (line 355-363)
      if (apiGlobal?.get) {
        (async () => {
          // For tabs: hideLoading from param controls if this fetch hides loading
          // When called from DocumentProvider (no selectedTab), always hide loading
          const shouldHideLoading = selectedTab ? hideLoading : true;

          consoleX({
            message: `${moduleId}|${pageName}` + (selectedTab ? `|${selectedTab}` : ''),
            emoji: shouldHideLoading ? '✅' : '⏱️',
            flag: shouldHideLoading ? 'success' : 'default',
            title: 'DONE',
            info: {
              module: moduleId,
              page: pageName,
              tab: selectedTab,
            },
          });

          // Pass both params and query from config to the request
          await apiGlobal.get({
            _id,
            hideLoading: shouldHideLoading,
            paramKey,
            selectedTab,
            url,
            params: configParams,
            query: configQuery,
            returnAsObject: configReturnAsObject, // Read from page config (e.g., PAGES.init.api.returnAsObject)
          });
        })();
      }
    }

    // Cleanup: Remove tracker to allow re-execution on next mount
    return () => {
      const currentId = _id;
      const currentParamKey = paramKey;
      const cleanupModuleId = moduleId;
      const cleanupPageName = pageName;

      setTimeout(() => {
        const oldKey = `${cleanupModuleId || 'none'}-${cleanupPageName || 'none'}-${currentParamKey || 'none'}-${currentId || 'none'}-${selectedTab || 'none'}`;
        executionTracker.delete(oldKey);
      }, 1000);
    };
  }, [
    apiGlobal?.done,
    apiGlobal?.get,
    params,
    storedCurrentPage?.data,
    storedCurrentPage?.moduleId,
    storedCurrentPage?.name,
    storedTransition,
    confirmationTrigger,
    storedSubscription?._id,
    storedCompany?._id,
    filterByCompany,
  ]); // Re-run when apiGlobal, params, data, moduleId, page name, transition, confirmation trigger, subscription, or company changes

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // NOTE: Effect 2 was removed because it was redundant.
  // The main effect (lines 100-257) already handles the !paramKey case by calling done().
  // Effect 2 was causing done() to be called twice.

  // EFFECT 3: Handle post-login/F5 edge case for LIST pages
  // After login or F5, the main effect may run before apiGlobal is set (useApiInitializer hasn't run yet).
  // This causes the main effect to return early at line 294 (!apiGlobal?.done).
  // Since apiGlobal is not in the main effect's deps, it doesn't re-run when apiGlobal becomes available.
  // This effect specifically handles that case by calling done() when:
  // 1. apiGlobal becomes available (done exists)
  // 2. Module is confirmed (confirmedModuleRef is set)
  // 3. Tracker doesn't have entry (done wasn't called by main effect)
  // 4. No paramKey (list pages only - document pages are handled by EFFECT 4)
  useEffect(() => {
    // Only for list pages (no paramKey)
    if (paramKey !== undefined) return;

    // Wait for apiGlobal to be available
    if (!apiGlobal?.done) return;

    // Wait for module to be confirmed
    if (!confirmedModuleRef.current) return;

    const moduleId = storedCurrentPage?.moduleId;
    const pageName = storedCurrentPage?.name;
    if (!moduleId || !pageName) return;

    // CRITICAL: Skip if page expects params (has :_id in URL) but paramKey is undefined
    // This happens during navigation when storedCurrentPage still has old page's config
    // Document pages should be handled by EFFECT 4, not EFFECT 3
    const apiUrl = url || storedCurrentPage?.api?.url;
    const pageExpectsId = apiUrl && apiUrl.includes(':_id');
    if (pageExpectsId) return; // This is a document page, not a list page - skip

    // Check if main effect already handled this (tracker has entry)
    const executionKey = `${moduleId}-${pageName}-none-none-${selectedTab || 'none'}`;
    if (executionTracker.has(executionKey)) return;

    // Mark as executed to prevent duplicate calls
    executionTracker.set(executionKey, true);

    // Call done() to hide loading screen
    // NOTE: apiUrl is already declared above (line 525)
    const resolvedAF = autoFetch ?? storedCurrentPage?.api?.autoFetch ?? true;
    const shouldHideLoading = hideLoading || (!apiUrl && resolvedAF);

    consoleX({
      message: `${moduleId}|${pageName}` + (selectedTab ? `|${selectedTab}` : ''),
      emoji: '🔄',
      flag: 'info',
      title: 'DONE (post-login)',
      info: { module: moduleId, page: pageName },
    });

    apiGlobal.done(shouldHideLoading);
  }, [apiGlobal?.done, storedCurrentPage?.moduleId, storedCurrentPage?.name]);

  // EFFECT 4: FALLBACK for document pages (with paramKey)
  // NOTE: With the main effect now having apiGlobal in dependencies, this effect
  // should rarely execute its logic (main effect handles most cases).
  // This serves as a safety net for edge cases where main effect might miss execution.
  useEffect(() => {
    // Only for document pages (with paramKey)
    if (!paramKey) return;

    // Wait for apiGlobal to be available
    if (!apiGlobal?.get) return;

    // CRITICAL: Wait for subscription to be loaded before making API calls
    // On F5, the subscription may not be ready yet, causing requests without x-subscriptionid header
    // This would result in backend returning null (document not found in subscription scope)
    // EXCEPTION: Pages with fetchWithoutSubscription: true don't need subscription (e.g., account pages)
    const fetchWithoutSubscription = storedCurrentPage?.api?.fetchWithoutSubscription;
    if (!fetchWithoutSubscription && !storedSubscription?._id) return;

    // NOTE: filterByCompany for document pages - wait for company if needed
    if (filterByCompany && !storedCompany?._id) return;

    const _id = params?.[paramKey];
    const moduleId = storedCurrentPage?.moduleId;
    const pageName = storedCurrentPage?.name;
    const apiUrl = url || storedCurrentPage?.api?.url;

    if (!moduleId || !pageName) return;
    if (!_id || _id === 'add') return; // Skip for new documents
    if (!apiUrl) return; // Skip if no API URL

    // Module confirmation (fallback - main effect should handle this in most cases)
    const currentModulePage = `${moduleId}|${pageName}`;
    if (!confirmedModuleRef.current) {
      pendingModuleRef.current = currentModulePage;
      confirmedModuleRef.current = currentModulePage;
      globalConfirmedModules.add(moduleId);
    } else if (confirmedModuleRef.current !== currentModulePage) {
      return; // Stale component
    }

    // Check if main effect already handled this (tracker has entry)
    const executionKey = `${moduleId}-${pageName}-${paramKey}-${_id}-${selectedTab || 'none'}`;
    if (executionTracker.has(executionKey)) return;

    // Skip if we just saved or refreshed (prevent duplicate fetches)
    if (wasJustSaved() || wasJustRefreshed()) return;

    // Mark as executed to prevent duplicate calls
    executionTracker.set(executionKey, true);

    const configParams = storedCurrentPage?.api?.params;
    const configQuery = storedCurrentPage?.api?.query;
    const configReturnAsObject = storedCurrentPage?.api?.returnAsObject;

    // For document pages, we need to call get() to fetch the data
    // hideLoading is true for the active tab (selectedTab matches or no selectedTab)
    const shouldHideLoading = selectedTab ? hideLoading : true;

    consoleX({
      message: `${moduleId}|${pageName}` + (selectedTab ? `|${selectedTab}` : ''),
      emoji: '🔄',
      flag: 'info',
      title: 'GET (post-F5)',
      info: { module: moduleId, page: pageName, _id },
    });

    apiGlobal.get({
      _id,
      hideLoading: shouldHideLoading,
      paramKey,
      selectedTab,
      url: apiUrl,
      params: configParams,
      query: configQuery,
      returnAsObject: configReturnAsObject, // Read from page config (e.g., PAGES.init.api.returnAsObject)
    });
    // NOTE: hideLoading is intentionally NOT in dependencies
    // Changes to hideLoading should only affect loading UI, not trigger a new fetch
    // This prevents duplicate GETs when switching tabs (hideLoading changes but data is already loaded)
  }, [
    apiGlobal?.get,
    storedCurrentPage?.moduleId,
    storedCurrentPage?.name,
    storedCurrentPage?.api?.url,
    params,
    paramKey,
    selectedTab,
    storedSubscription?._id,
    storedCompany?._id,
    filterByCompany,
  ]);
  // }, [apiGlobal?.get, storedCurrentPage?.moduleId, storedCurrentPage?.name, storedCurrentPage?.api?.url, params, paramKey, selectedTab, hideLoading, storedSubscription?._id, storedCompany?._id, filterByCompany]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────

  return { ...apiGlobal };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default useApi;

// *******************************************************************************
// HELPERS
// *******************************************************************************
